/**
 * 弹层组件
 */

import React, { useRef, useEffect, useCallback, useState } from 'react';
import cn from 'classnames';
import styles from './styles.module.scss';

export interface Props {
  open: boolean;
  tapMaskClose?: boolean;
  animationDuration?: number;
  children: React.ReactNode;
  /**
   * @deprecated use anchor
   */
  archor?: 'top' | 'bottom' | 'center';
  anchor?: 'top' | 'bottom' | 'center';
  bodyClassNames?: string;
  modalClassNames?: string;
  onClose(): void;
  onClosed?(): void;
  destroyOnClose?: boolean;
}

export default function Modal(props: Props) {
  const timer = useRef<NodeJS.Timeout>();
  const anchor = props.anchor || props.archor;
  const prevOpenState = useRef<boolean>();
  const modalRef = useRef<HTMLDivElement>(null);
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const modalMaskRef = useRef<HTMLDivElement>(null);
  const [closed, setClosed] = useState(!props.open);

  const open = useCallback(() => {
    const el = modalRef.current;
    const bodyEl = modalBodyRef.current;
    const maskEl = modalMaskRef.current;

    if (!el || !bodyEl || !maskEl) return;

    el.classList.add(styles.open);
    el.style.opacity = '0';

    setClosed(false);

    if (!props.animationDuration) {
      return;
    }

    timer.current = setTimeout(() => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      clearTimeout(timer.current!);

      // 强制同步
      const height = bodyEl.offsetHeight;

      bodyEl.style.transition = '';
      switch (anchor) {
        case 'bottom':
          bodyEl.style.transform = `translate3d(0, ${height}px, 0)`;
          break;
        case 'top':
          bodyEl.style.transform = `translate3d(0, -${height}px, 0)`;
          break;
        case 'center':
          bodyEl.style.opacity = '0.5';
          bodyEl.style.transform = 'scale(1.2)';
          break;
        default:
      }
      maskEl.style.opacity = '1';
      el.style.opacity = '1';

      timer.current = setTimeout(() => {
        switch (anchor) {
          case 'top':
          case 'bottom':
            bodyEl.style.transition = `transform ${props.animationDuration}ms`;
            bodyEl.style.transform = `translate3d(0, 0, 0)`;
            break;
          case 'center':
            bodyEl.style.transition = `transform ${props.animationDuration}ms,opacity ${props.animationDuration}ms`;
            bodyEl.style.opacity = '1';
            bodyEl.style.transform = 'scale(1)';
            break;
          default:
        }
      }, 17);

      timer.current = setTimeout(() => {
        clearTimeout(timer.current!);
        //
      }, props.animationDuration);
    }, 17);
  }, [anchor, props.animationDuration]);

  const close = useCallback(() => {
    const el = modalRef.current;
    const bodyEl = modalBodyRef.current;
    const maskEl = modalMaskRef.current;

    if (!el || !bodyEl || !maskEl) return;
    if (!el.classList.contains(styles.open)) return;

    if (!props.animationDuration) {
      el.classList.remove(styles.open);
      props.onClosed?.();
      setClosed(true);

      return;
    }

    clearTimeout(timer.current!);
    // 强制同步
    const height = bodyEl.offsetHeight;

    switch (anchor) {
      case 'top':
        bodyEl.style.transform = `translate3d(0, -${height}px, 0)`;
        bodyEl.style.transition = `transform ${props.animationDuration}ms`;
        break;
      case 'bottom':
        bodyEl.style.transform = `translate3d(0, ${height}px, 0)`;
        bodyEl.style.transition = `transform ${props.animationDuration}ms`;
        break;
      case 'center':
        bodyEl.style.opacity = '0';
        bodyEl.style.transition = `opacity ${props.animationDuration}ms`;
        break;
      default:
    }
    maskEl.style.opacity = '0';
    timer.current = setTimeout(() => {
      el.classList.remove(styles.open);
      props.onClosed?.();
      setClosed(true);
    }, props.animationDuration);
  }, [props]);

  useEffect(() => {
    if (prevOpenState.current === props.open) return;

    if (props.open) {
      document.body.style.overflow = 'hidden';
      open();
    } else {
      document.body.style.overflow = '';
      close();
    }

    prevOpenState.current = props.open;

    return () => {
      document.body.style.overflow = '';
    };
  }, [close, open, props.open]);

  return (
    <div
      ref={modalRef}
      className={cn(styles.modal, props.modalClassNames, {
        [styles.archorTop]: anchor === 'top',
        [styles.archorBottom]: anchor === 'bottom',
        [styles.archorCenter]: anchor === 'center',
      })}
    >
      <div
        ref={modalMaskRef}
        className={styles.modalMask}
        onClick={e => {
          e.stopPropagation();
          if (props.tapMaskClose) {
            props.onClose();
          }
        }}
      />
      <div
        ref={modalBodyRef}
        className={cn(styles.modalBody, props.bodyClassNames)}
      >
        {props.destroyOnClose && closed ? null : props.children}
      </div>
    </div>
  );
}

Modal.defaultProps = {
  archor: 'top',
  tapMaskClose: true,
  animationDuration: 300,
  destroyOnClose: false,
  onClose: () => {},
  onClosed: () => {},
};
